import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError, setToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, User, Lock } from "lucide-react";
import { useHideEmergentBadge } from "@/hooks/useHideEmergentBadge";

export default function Login() {
  useHideEmergentBadge();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      navigate(user.role === "admin" ? "/admin" : "/merchant", { replace: true });
    }
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      toast.error("Enter username and password");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { username: username.trim(), password });
      if (data?.token) setToken(data.token);
      await refresh();
      toast.success(`Welcome${data.user.name ? `, ${data.user.name.split(" ")[0]}` : ""}`);
      navigate(data.user.role === "admin" ? "/admin" : "/merchant", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      <div className="relative overflow-hidden hidden md:flex flex-col justify-between p-12 bg-[#002fa7] text-white">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium opacity-80 hover:opacity-100">
          <ArrowLeft className="w-4 h-4" /> Back home
        </Link>
        <div>
          <div className="text-xs uppercase tracking-[0.24em] opacity-70 mb-4">Staff sign-in</div>
          <h1 className="font-display text-5xl font-extrabold tracking-tighter leading-[0.95] mb-6">
            Run your<br />loyalty program.
          </h1>
          <p className="opacity-80 text-lg max-w-md">
            For merchants and platform admins. Your account was created for you — just sign in.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm opacity-70">
          <Sparkles className="w-4 h-4" /> SparkLoop
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#f9f9fb]">
        <div className="w-full max-w-md">
          <Link to="/" className="md:hidden inline-flex items-center gap-2 text-sm font-medium text-[#5a5a65] mb-8">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <div className="text-xs uppercase tracking-[0.24em] text-[#002fa7] font-semibold mb-3">Sign in</div>
          <h2 className="font-display text-4xl font-extrabold tracking-tight mb-2">Welcome back.</h2>
          <p className="text-[#5a5a65] mb-8">Use the credentials you were given by SparkLoop.</p>

          <form onSubmit={submit} className="space-y-4" autoComplete="off">
            {/* Honeypot — disables Safari/Chrome autofill on the real inputs below. */}
            <input type="text" name="username" autoComplete="username" style={{ display: "none" }} tabIndex={-1} />
            <input type="password" name="password" autoComplete="current-password" style={{ display: "none" }} tabIndex={-1} />
            <div>
              <label className="text-xs uppercase tracking-[0.16em] font-semibold text-[#5a5a65] mb-2 block">
                Username
              </label>
              <div className="relative">
                <User className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#5a5a65]" />
                <Input
                  data-testid="login-username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="jane"
                  autoComplete="off"
                  name="sl-user-input"
                  className="h-12 pl-11 rounded-xl border-[#e5e5eb] bg-white focus-visible:ring-[#002fa7]/20 focus-visible:border-[#002fa7]"
                  autoFocus
                  required
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.16em] font-semibold text-[#5a5a65] mb-2 block">
                Password
              </label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#5a5a65]" />
                <Input
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  name="sl-pw-input"
                  className="h-12 pl-11 rounded-xl border-[#e5e5eb] bg-white focus-visible:ring-[#002fa7]/20 focus-visible:border-[#002fa7]"
                  required
                />
              </div>
            </div>
            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#002fa7] hover:bg-[#002585] text-white rounded-xl text-base font-semibold"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
